import { useState, useEffect, useRef, useCallback, useMemo, lazy, Suspense } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon,
  CheckIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserPlusIcon,
  ArrowUturnLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  ChatBubbleLeftEllipsisIcon,
  PaperAirplaneIcon,
  ArrowTopRightOnSquareIcon,
  Squares2X2Icon,
  ListBulletIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useNode } from '../contexts/NodeContext';
import { useAuth } from '../contexts/AuthContext';
import {
  getNodeAlertNames,
  getNodeAlerts,
  getNodeAlertStats,
  acknowledgeNodeAlert,
  unacknowledgeNodeAlert,
  assignNodeAlert,
  resolveNodeAlert,
  cancelNodeAlert,
  deleteNodeAlert,
  getNodeCameras,
  getNodeUsers,
  getNodeAlertActions,
  addNodeAlertComment,
  getNodeModelEventImage,
} from '../services/api.service';
import { tableToModel } from '../api/hexAlerts.core';
import {
  AlertListCards,
  AlertListFilters,
  AlertListTable,
  Avatar,
  Modal,
  PageTabs,
  RecordPager,
  SegmentedToggle,
  alertName,
  alertRangeFrom,
  initialsOf,
  type AlertRangeKey,
} from '@trinetra/shared';

/**
 * The alert name, the person bubble and its initials now live in @trinetra/shared — the alert
 * list, the alert detail page and the local node's copies of both have to spell a person and
 * an alert the same way. Re-exported because AlertDetailPage has always imported them here.
 */
export { alertName, Avatar, initialsOf };

/**
 * The alert record the list opens in a modal.
 *
 * A dynamic import rather than a static one for exactly one reason: AlertDetailPage already
 * imports this module's own parts (Field, ActionModal, alertName…), and a static edge back
 * would close that into a cycle. It buys no code-splitting — the routed page imports the same
 * module statically — so treat it as a dependency-direction fix, not a performance one.
 */
const AlertDetailView = lazy(() =>
  import('./AlertDetailPage').then((m) => ({ default: m.AlertDetailView })),
);

const STATUSES = ['new', 'acknowledged', 'assigned', 'resolved', 'cancelled'] as const;
type Status = (typeof STATUSES)[number];

/** Tab labels, so the strip reads "Acknowledged" rather than a CSS-capitalised "acknowledged". */
const STATUS_LABEL: Record<Status, string> = {
  new: 'New',
  acknowledged: 'Acknowledged',
  assigned: 'Assigned',
  resolved: 'Resolved',
  cancelled: 'Cancelled',
};

const DEFAULT_PAGE_SIZE = 25;

export const badgeCls = 'px-1.5 py-0.5 text-[10px] font-bold uppercase rounded';

export function fmtTime(ts?: number) {
  if (!ts) return '—';
  let ms = Number(ts);
  if (ms < 1e12) ms *= 1000;
  const d = new Date(ms);
  return d.toLocaleDateString([], { day: '2-digit', month: 'short' }) + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export default function AlertsPage() {
  const { selectedNode } = useNode();
  const { user, hasPermission } = useAuth();
  const canManageAlerts = hasPermission('alerts.edit'); // backend also enforces
  /* A SEPARATE grant from alerts.edit — working an alert and erasing it are different powers.
     The cloud proxy gates DELETE on this one too, so withholding it here is not the only
     thing standing between a viewer and the row. */
  const canDeleteAlerts = hasPermission('alerts.delete');
  const nodeId = selectedNode?.nodeId || '';
  const uid = user?.id || null;
  const uname = user?.name || user?.email || null;

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const cameraId = searchParams.get('camera_id') || '';

  const [alerts, setAlerts] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  /**
   * Cards or rows — two presentations of the SAME page of alerts (see AlertListCards).
   *
   * Cards by default: half of triage is recognising the scene, and a queue of "Person
   * Detected · Main Gate" rows all read alike. The table is one click away and remembered,
   * because the operator who works twenty-five at a time wants the density.
   */
  const [view, setView] = useState<'cards' | 'table'>(
    () => (localStorage.getItem('alerts.view') === 'table' ? 'table' : 'cards'),
  );
  useEffect(() => { localStorage.setItem('alerts.view', view); }, [view]);

  const [statusFilter, setStatusFilter] = useState<Status | 'all'>('all');
  const [severity, setSeverity] = useState('');
  /**
   * The alert's NAME, as the card shows it — the same axis the alert board filters on.
   *
   * Called `typeFilter`, not `alertName`: this module already imports `alertName`, the helper
   * that DERIVES the displayed name from a row, and a state variable of that name shadows it
   * inside the component — where every call to it then fails at runtime, silently, because
   * this app's build does not typecheck.
   */
  const [typeFilter, setTypeFilter] = useState('');
  const [typeOptions, setTypeOptions] = useState<{ name: string; count: number }[]>([]);
  /**
   * Two search states, deliberately: `searchInput` is what's in the box and `search` is what
   * the node has been asked for. The box used to filter only on Enter, which reads as a dead
   * control; debouncing the copy that drives the request gives it live feedback without a
   * round trip through the tunnel per keystroke.
   */
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  /**
   * Defaults to "all time" rather than the last 24 hours: a site whose last alert is three
   * days old would otherwise open to an empty page that looks like a broken tunnel, not a
   * quiet site. The window is one dropdown away for the operators who want it.
   */
  const [range, setRange] = useState<AlertRangeKey>('all');
  // Paging is server-side, so the page size is part of the request — changing it refetches.
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  /**
   * Per-status totals behind the tab strip, from the node's /alerts/stats under the SAME
   * filters the list runs. Empty until the first response (and after a failure), which
   * renders the tabs label-only rather than with a row of confident zeroes.
   */
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [actionFor, setActionFor] = useState<{ alert: any; kind: 'resolve' | 'cancel' } | null>(null);
  /* Its own dialog rather than a third `kind` of the note modal: no text to type, the alert's
     name shown back so the operator can see what they are erasing, and a red confirm. */
  const [deleteFor, setDeleteFor] = useState<any | null>(null);
  const [deleting, setDeleting] = useState(false);
  /**
   * The row being read, as an INDEX into the page of alerts rather than an alert.
   *
   * Working alerts is a sweep down the list, so the record opens over it — keeping the filters,
   * the scroll position and the page — and the arrow keys move to the next one without closing.
   * `/alerts/:id` still exists for links out of here; the "Full page" button goes there.
   */
  const [viewingIndex, setViewingIndex] = useState<number | null>(null);
  const viewing = viewingIndex !== null ? alerts[viewingIndex] : null;
  const [assignFor, setAssignFor] = useState<any | null>(null);
  const [cameras, setCameras] = useState<{ id: string; name?: string }[]>([]);
  const cameraMap = useMemo(
    () => Object.fromEntries(cameras.map((c) => [c.id, c.name || c.id])),
    [cameras],
  );
  const [users, setUsers] = useState<any[]>([]);
  const userMap = useMemo(
    () => Object.fromEntries(users.map((u) => [u.id, u.full_name || u.name || u.email || u.id])),
    [users],
  );

  useEffect(() => {
    if (!nodeId) return;
    getNodeCameras(nodeId)
      .then((cams: any) => {
        const arr = Array.isArray(cams) ? cams : cams?.data || [];
        setCameras(arr.map((c: any) => ({ id: c.id, name: c.name })));
      })
      .catch(() => {});
    // Node users (local + provisioned cloud users with access) for the assign dropdown + name resolution.
    getNodeUsers(nodeId)
      .then((list: any[]) => setUsers(Array.isArray(list) ? list : []))
      .catch(() => setUsers([]));
  }, [nodeId]);

  // Everything except the status tab — the list sends this plus its status, the tab counts
  // send it WITHOUT one, which is what makes a tab's number the size of the queue behind it.
  const filterParams = useMemo(() => {
    const params: any = {};
    if (severity) params.severity = severity;
    if (search) params.search = search;
    if (cameraId) params.camera_id = cameraId;
    const from = alertRangeFrom(range);
    if (from) params.from = from;
    return params;
  }, [severity, search, cameraId, range]);

  /**
   * The list's params. `alert_name` is kept OUT of `filterParams`: that object also feeds the
   * status tab counts and the name options, and a tab reading "Open 3" under a type filter
   * would describe a queue the operator cannot see the rest of.
   */
  const listParams = useMemo(
    () => (typeFilter ? { ...filterParams, alert_name: typeFilter } : filterParams),
    [filterParams, typeFilter],
  );

  const fetchAlerts = useCallback(async () => {
    if (!nodeId) { setAlerts([]); setLoading(false); return; }
    setLoading(true);
    try {
      const params: any = { ...listParams, page, limit: pageSize, sort_by: 'event_timestamp', sort_order: 'desc' };
      if (statusFilter !== 'all') params.status = statusFilter;
      const res: any = await getNodeAlerts(nodeId, params);
      const rows = res?.data ?? res ?? [];
      setAlerts(Array.isArray(rows) ? rows : []);
      setTotal(res?.pagination?.total ?? rows.length ?? 0);
    } catch (err) {
      console.error('Failed to fetch alerts:', err);
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, [nodeId, listParams, page, pageSize, statusFilter]);

  const fetchCounts = useCallback(async () => {
    if (!nodeId) { setCounts({}); return; }
    try {
      const stats: any = await getNodeAlertStats(nodeId, filterParams);
      // A status with nothing behind it is absent from by_status — seed the zeroes so the
      // tab shows "0" rather than dropping its badge and shifting the strip.
      const zeroes = Object.fromEntries(STATUSES.map((s) => [s, 0]));
      const byStatus = Object.fromEntries((stats?.by_status || []).map((r: any) => [r.status, Number(r.count)]));
      setCounts({ ...zeroes, ...byStatus, all: Number(stats?.total ?? 0) });
    } catch {
      setCounts({});
    }
  }, [nodeId, filterParams]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);
  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  /**
   * The alert's picture, streamed through the node tunnel.
   *
   * A node's `image_urls` are absolute URLs INSIDE that node — the cloud browser can't reach
   * them, so the bytes come back as a blob from the same proxy the alert record uses
   * (`getNodeModelEventImage`, by model + event id + image type). The list only asks for ONE
   * frame per alert, `fullFrame` where the model produced one, and the shared resolver holds
   * the request until the card is actually on screen: a page of twenty-five is twenty-five
   * round trips over a link that may be a long way away.
   *
   * A tunnel 404 or timeout still resolves as a (JSON/text) Blob, so only real images count —
   * otherwise the card would render an <img> of an error message.
   */
  const loadThumbnail = useCallback(
    async (alert: any): Promise<string | null> => {
      const model = tableToModel(alert?.source_table);
      if (!nodeId || !model || !alert?.event_id) return null;
      const urls = alert?.image_urls && typeof alert.image_urls === 'object' ? alert.image_urls : {};
      const type = urls.fullFrame ? 'fullFrame' : Object.keys(urls).find((k) => urls[k]);
      if (!type) return null;
      try {
        const blob: any = await getNodeModelEventImage(nodeId, model, alert.event_id, type);
        if (!blob || (blob.type && !blob.type.startsWith('image/'))) return null;
        return URL.createObjectURL(blob);
      } catch {
        return null;
      }
    },
    [nodeId],
  );

  /**
   * Switching site starts this page over.
   *
   * The list refetches on its own (every fetch keys off `nodeId`), but the things POINTING at
   * rows do not: `?camera_id=` names a camera the new site hasn't got, so the queue comes back
   * empty and reads as a quiet site rather than as a filter that can never match; the open
   * record is an INDEX into a page of alerts that has just been replaced; and page 4 of the old
   * site's queue is not where the new one should open.
   */
  const firstNodeRef = useRef(true);
  useEffect(() => {
    if (firstNodeRef.current) { firstNodeRef.current = false; return; }
    setPage(1);
    setViewingIndex(null);
    setActionFor(null);
    setAssignFor(null);
    setDeleteFor(null);
    if (searchParams.get('camera_id')) {
      const next = new URLSearchParams(searchParams);
      next.delete('camera_id');
      setSearchParams(next, { replace: true });
    }
    // searchParams is read, not tracked: this must fire on a site change and nothing else.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId]);

  // Debounced: the box filters as you type without a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const setCameraFilter = (id: string) => {
    if (id) searchParams.set('camera_id', id); else searchParams.delete('camera_id');
    setSearchParams(searchParams, { replace: true });
    setPage(1);
  };

  const clearFilters = () => {
    setSeverity(''); setTypeFilter(''); setSearchInput(''); setSearch(''); setRange('all'); setPage(1);
    if (cameraId) { searchParams.delete('camera_id'); setSearchParams(searchParams, { replace: true }); }
  };

  /** The names to offer, under everything EXCEPT the name filter itself. */
  const fetchAlertNames = useCallback(async () => {
    if (!nodeId) { setTypeOptions([]); return; }
    try {
      setTypeOptions(await getNodeAlertNames(nodeId, filterParams));
    } catch {
      // An older node has no such endpoint; the rest of the bar keeps working.
      setTypeOptions([]);
    }
  }, [nodeId, filterParams]);

  useEffect(() => { void fetchAlertNames(); }, [fetchAlertNames]);

  // A name whose last alert has just aged out of the window would otherwise leave the list
  // filtered by something no longer on offer.
  useEffect(() => {
    if (typeFilter && typeOptions.length && !typeOptions.some((n) => n.name === typeFilter)) {
      setTypeFilter('');
      setPage(1);
    }
  }, [typeOptions, typeFilter]);

  const refresh = () => { fetchAlerts(); fetchCounts(); fetchAlertNames(); };

  // A lifecycle change moves the alert between tabs, so the counts have to move with it.
  const apply = (id: string, updated: any) => {
    setAlerts((prev) => prev.map((a) => (a.id === id ? { ...a, ...updated } : a)));
    fetchCounts();
  };

  const tabs = useMemo(() => ([
    { key: 'all', label: 'All', count: counts.all ?? null },
    ...STATUSES.map((s) => ({ key: s, label: STATUS_LABEL[s], count: counts[s] ?? null })),
  ]), [counts]);

  const doAck = async (a: any) => { try { apply(a.id, await acknowledgeNodeAlert(nodeId, a.id, uid, uname)); } catch (e) { console.error(e); } };
  const doUnack = async (a: any) => { try { apply(a.id, await unacknowledgeNodeAlert(nodeId, a.id, uid, uname)); } catch (e) { console.error(e); } };
  const doAssignMe = async (a: any) => { if (!uid) return; try { apply(a.id, await assignNodeAlert(nodeId, a.id, uid, uid, uname, uname)); } catch (e) { console.error(e); } };
  const doAssign = async (a: any, assigneeId: string, assigneeName: string) => { try { apply(a.id, await assignNodeAlert(nodeId, a.id, assigneeId, uid, assigneeName, uname)); } catch (e) { console.error(e); } };

  /**
   * Erase the alert on the node. Not a lifecycle move — the row and its action history go.
   *
   * REFETCHES rather than splicing the row out: paging is server-side (and, here, a tunnel
   * away), so a page that quietly loses a row disagrees with its own total and never pulls up
   * the row that should have moved in from the next page. Deleting the last row of a page past
   * the first leaves that page nonexistent, so step back one and let the effect fetch it.
   */
  const doDelete = async (a: any) => {
    setDeleting(true);
    try {
      await deleteNodeAlert(nodeId, a.id);
      if (viewing?.id === a.id) setViewingIndex(null);
      if (alerts.length === 1 && page > 1) { setPage((p) => p - 1); fetchCounts(); }
      else refresh();
      setDeleteFor(null);
    } catch (e) {
      console.error('Failed to delete alert:', e);
    } finally {
      setDeleting(false);
    }
  };
  const doResolve = async (a: any, note: string) => { try { apply(a.id, await resolveNodeAlert(nodeId, a.id, uid, note, uname)); } catch (e) { console.error(e); } };
  const doCancel = async (a: any, reason: string) => { try { apply(a.id, await cancelNodeAlert(nodeId, a.id, uid, reason, uname)); } catch (e) { console.error(e); } };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div className="space-y-4 p-3 sm:p-6">
      {/* ── Header ── */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-baseline gap-3">
          <h1 className="text-xl font-semibold text-foreground">Alert List</h1>
          {total > 0 && <span className="text-[13px] text-muted-foreground">({total} alerts)</span>}
          <span className="hidden md:inline text-[13px] text-muted-foreground">
            Acknowledge, assign and resolve alerts for <span className="font-medium text-foreground">{selectedNode?.name || nodeId || '—'}</span>
          </span>
        </div>
        <button
          onClick={refresh}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors"
          title="Refresh"
        >
          <ArrowPathIcon className="h-4 w-4" />
        </button>
      </div>

      {/* ── Filter Bar ── */}
      <AlertListFilters
        search={searchInput}
        onSearchChange={setSearchInput}
        severity={severity}
        onSeverityChange={(v) => { setSeverity(v); setPage(1); }}
        alertName={typeFilter}
        onAlertNameChange={(v) => { setTypeFilter(v); setPage(1); }}
        alertNames={typeOptions}
        cameraId={cameraId}
        onCameraChange={setCameraFilter}
        cameras={cameras}
        range={range}
        onRangeChange={(v) => { setRange(v); setPage(1); }}
        pageSize={pageSize}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
        onClear={clearFilters}
      />

      {/* ── No site selected ── */}
      {!nodeId ? (
        <div className="rounded-xl border border-border bg-card shadow-card py-16 text-center text-muted-foreground text-sm">
          Select a site from the sidebar to view its alerts.
        </div>
      ) : (
        /* ── Status tabs + table ──
           One card: the tabs are a view onto the table under them, not a separate control, and
           a gap between the two made them read as a second filter bar. */
        <div className="rounded-xl border border-border bg-card shadow-card overflow-hidden">
          {/* The view switch rides on the tab strip's own line — it belongs to the list under
              it, and a second toolbar row for one control would push the alerts down. */}
          <div className="flex items-center justify-between gap-3 pr-3">
            <PageTabs
              tabs={tabs}
              active={statusFilter}
              onChange={(key) => { setStatusFilter(key as Status | 'all'); setPage(1); }}
              className="min-w-0 flex-1 px-2"
            />
            <SegmentedToggle<'cards' | 'table'>
              aria-label="Alert list view"
              value={view}
              onChange={setView}
              options={[
                { key: 'cards', label: 'Cards', icon: Squares2X2Icon, title: 'Cards — the alert’s own frame' },
                { key: 'table', label: 'Table', icon: ListBulletIcon, title: 'Table — more alerts per screen' },
              ]}
            />
          </div>

          {/* Both views take the same props: same page, same actions, same record opened. */}
          {(() => {
            const listProps = {
              alerts,
              loading,
              cameraMap,
              userMap,
              canManage: canManageAlerts,
              canDelete: canDeleteAlerts,
              loadThumbnail,
              onOpen: setViewingIndex,
              onAcknowledge: doAck,
              onAssign: setAssignFor,
              onResolve: (a: any) => setActionFor({ alert: a, kind: 'resolve' as const }),
              onCancel: (a: any) => setActionFor({ alert: a, kind: 'cancel' as const }),
              onReopen: doUnack,
              onDelete: (a: any) => setDeleteFor(a),
              emptyHint:
                statusFilter !== 'all' || severity || typeFilter || search || cameraId || range !== 'all'
                  ? 'Try another status tab, or clear the filters above.'
                  : 'Alerts appear here as soon as this site raises one.',
            };
            return view === 'cards' ? <AlertListCards {...listProps} /> : <AlertListTable {...listProps} />;
          })()}

          {/* Pagination */}
          {!loading && alerts.length > 0 && (
            <div className="flex items-center justify-between border-t border-border px-4 py-3">
              <span className="text-[12px] text-muted-foreground">
                {/* Echoing the size the request actually used makes a mismatch with the
                    filter-bar dropdown obvious instead of something you must count rows for. */}
                {from}–{to} of {total} · Page {page} of {totalPages} · {pageSize} per page
              </span>
              <div className="flex items-center gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="flex h-8 items-center gap-1 rounded-lg border border-border px-3 text-[12px] text-foreground hover:bg-muted disabled:opacity-30 transition-colors"
                >
                  <ChevronLeftIcon className="h-3.5 w-3.5" /> Prev
                </button>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="flex h-8 items-center gap-1 rounded-lg border border-border px-3 text-[12px] text-foreground hover:bg-muted disabled:opacity-30 transition-colors"
                >
                  Next <ChevronRightIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {assignFor && (
        <AssignModal
          users={users}
          currentUser={{ id: uid, name: uname }}
          onClose={() => setAssignFor(null)}
          onConfirm={(assigneeId: string, assigneeName: string) => {
            doAssign(assignFor, assigneeId, assigneeName);
            setAssignFor(null);
          }}
        />
      )}
      {/* ── Alert Detail Modal ──
          The common Modal shell at full width (the same one the report tables open an event in),
          titled with the alert's own name. Left/right step through the page's rows; Escape
          closes. Closing refetches, so a status changed in here shows in the row behind it. */}
      <Modal
        open={!!viewing}
        onClose={() => { setViewingIndex(null); refresh(); }}
        size="full"
        ariaLabel="Alert details"
        title={viewing && (
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-brand">{alertName(viewing)}</span>
            <span className="text-[13px] font-normal text-muted-foreground">· Alert Details</span>
          </span>
        )}
        subtitle={viewing && [cameraMap[viewing.camera_id] || viewing.camera_id, fmtTime(viewing.event_timestamp)].filter(Boolean).join(' · ')}
        headerActions={viewing && (
          <>
            <RecordPager
              index={viewingIndex ?? 0}
              count={alerts.length}
              onIndexChange={setViewingIndex}
              noun="alerts"
            />
            <button
              type="button"
              onClick={() => navigate(`/alerts/${viewing.id}`)}
              title="Open the full alert page"
              className="flex h-8 items-center gap-1.5 rounded-lg border border-border px-2.5 text-[12px] text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" /> Full page
            </button>
          </>
        )}
      >
        {/* Keyed on the alert id so stepping to the next row refetches instead of showing the
            previous alert's data while the new one loads. */}
        {viewing && (
          <Suspense fallback={<div className="p-6"><div className="h-64 animate-pulse rounded-lg bg-muted" /></div>}>
            <AlertDetailView key={viewing.id} alertId={viewing.id} showTitle={false} />
          </Suspense>
        )}
      </Modal>

      {actionFor && (
        <ActionModal
          kind={actionFor.kind}
          onClose={() => setActionFor(null)}
          onConfirm={(value: string) => {
            const { alert, kind } = actionFor;
            if (kind === 'resolve') doResolve(alert, value); else doCancel(alert, value);
            setActionFor(null);
          }}
        />
      )}

      {deleteFor && (
        <DeleteAlertModal
          alert={deleteFor}
          name={alertName(deleteFor)}
          camera={cameraMap[deleteFor.camera_id] || deleteFor.camera_id}
          busy={deleting}
          onClose={() => setDeleteFor(null)}
          onConfirm={() => doDelete(deleteFor)}
        />
      )}
    </div>
  );
}

/**
 * The confirm for a delete — deliberately NOT the note dialog with a third mode.
 *
 * It states WHAT is about to go (name, camera, when), because the bin was clicked on one row
 * of twenty-five identical-looking ones and "Are you sure?" cannot be answered without
 * knowing which. It says the deletion is permanent, and names the two things that are NOT
 * this — Resolve and Cancel — since an operator reaching for "make this go away" usually
 * wants one of those and keeps the record.
 *
 * `busy` disables both buttons while the DELETE is in flight, which over a tunnel is long
 * enough for an impatient second click to reach an id that has already gone.
 */
export function DeleteAlertModal({ alert, name, camera, busy, onClose, onConfirm }: any) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={busy ? undefined : onClose}>
      <div className="w-full max-w-md rounded-2xl border border-border bg-card shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-50 dark:bg-red-950">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
          </span>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-foreground">Delete this alert?</h2>
            <p className="mt-1 text-[13px] text-muted-foreground">
              It is removed permanently from the node, together with its action history. This cannot be undone.
            </p>
          </div>
        </div>

        <div className="mt-4 rounded-lg border border-border bg-muted px-3 py-2.5">
          <p className="truncate text-[13px] font-semibold text-foreground" title={name}>{name}</p>
          <p className="mt-0.5 truncate text-[12px] text-muted-foreground">
            {[camera, alert?.event_timestamp ? fmtTime(alert.event_timestamp) : null].filter(Boolean).join(' · ') || '—'}
          </p>
        </div>

        <p className="mt-3 text-[12px] text-muted-foreground">
          To close an alert but keep the record, use <span className="font-medium text-foreground">Resolve</span> or{' '}
          <span className="font-medium text-foreground">Cancel</span> instead.
        </p>

        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            disabled={busy}
            className="h-9 px-4 rounded-lg border border-border text-[13px] text-foreground hover:bg-muted disabled:opacity-50 transition-colors"
          >
            Keep it
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="flex h-9 items-center gap-1.5 px-4 rounded-lg bg-red-600 text-white text-[13px] font-medium hover:bg-red-700 disabled:opacity-60 transition-colors"
          >
            <TrashIcon className="h-4 w-4" />
            {busy ? 'Deleting…' : 'Delete permanently'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Assign dialog: quick "Assign to me" + a dropdown of every node user (local + cloud-with-access). */
export function AssignModal({ users, currentUser, onClose, onConfirm }: any) {
  const [sel, setSel] = useState('');
  const nameFor = (u: any) => u.full_name || u.name || u.email || u.id;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-border bg-card shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-foreground mb-3">Assign alert</h2>
        {currentUser?.id && (
          <button
            onClick={() => onConfirm(currentUser.id, currentUser.name || 'Me')}
            className="mb-3 w-full rounded-lg border border-border px-3 py-2 text-left text-[13px] text-foreground hover:bg-muted transition-colors"
          >
            Assign to me{currentUser.name ? ` (${currentUser.name})` : ''}
          </button>
        )}
        <label className="block text-[12px] font-medium text-muted-foreground mb-1">Assign to user</label>
        <select
          value={sel}
          onChange={(e) => setSel(e.target.value)}
          className="w-full h-9 rounded-lg border border-border bg-background px-3 text-[13px] text-foreground focus:border-brand focus:outline-none"
        >
          <option value="">Select a user…</option>
          {users.map((u: any) => (
            <option key={u.id} value={u.id}>
              {nameFor(u)}{u.user_type === 'cloud' ? ' · cloud' : ''}
            </option>
          ))}
        </select>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="h-9 px-4 rounded-lg border border-border text-[13px] text-foreground hover:bg-muted transition-colors">
            Cancel
          </button>
          <button
            onClick={() => {
              const u = users.find((x: any) => x.id === sel);
              if (u) onConfirm(u.id, nameFor(u));
            }}
            disabled={!sel}
            className="h-9 px-4 rounded-lg bg-brand text-white text-[13px] font-medium hover:bg-brand/90 disabled:opacity-40 transition-colors"
          >
            Assign
          </button>
        </div>
      </div>
    </div>
  );
}

export const ACTION_LABEL: Record<string, string> = {
  acknowledged: 'acknowledged this alert',
  unacknowledged: 'unacknowledged this alert',
  assigned: 'assigned this alert',
  resolved: 'resolved this alert',
  cancelled: 'cancelled this alert',
  reopened: 'reopened this alert',
  commented: 'commented',
};

/** Workflow-history headline + dot colour per audit action. */
const HISTORY_TITLE: Record<string, string> = {
  created: 'Detection Created',
  acknowledged: 'Alert Acknowledged',
  unacknowledged: 'Alert Reopened',
  assigned: 'Alert Assigned',
  resolved: 'Alert Resolved',
  cancelled: 'Marked False Positive',
  reopened: 'Alert Reopened',
};
const HISTORY_DOT: Record<string, string> = {
  created: 'bg-blue-500',
  acknowledged: 'bg-amber-500',
  unacknowledged: 'bg-blue-500',
  assigned: 'bg-indigo-500',
  resolved: 'bg-emerald-500',
  cancelled: 'bg-red-500',
  reopened: 'bg-blue-500',
};

/** Audit rows carry ISO strings; alert timestamps can be epoch seconds or ms. */
function toMs(value: any) {
  if (value == null || value === '') return NaN;
  if (typeof value === 'number' || /^\d+$/.test(String(value))) {
    let ms = Number(value);
    if (ms < 1e12) ms *= 1000;
    return ms;
  }
  return new Date(value).getTime();
}

/** "8h ago" for comments, absolute date once it's over a week old. */
function relTime(value: any) {
  const ms = toMs(value);
  if (Number.isNaN(ms)) return '';
  const secs = Math.max(0, (Date.now() - ms) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ms).toLocaleDateString();
}

/** "21 Jul, 04:24 am" for the workflow-history sub-line. */
function histTime(value: any) {
  const ms = toMs(value);
  if (Number.isNaN(ms)) return '';
  const d = new Date(ms);
  return `${d.toLocaleDateString([], { day: '2-digit', month: 'short' })}, ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }).toLowerCase()}`;
}

const CARD_TONE: Record<string, string> = {
  emerald: 'border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-400 dark:hover:bg-emerald-950/70',
  red: 'border-red-300 bg-red-50 text-red-600 hover:bg-red-100 dark:border-red-900 dark:bg-red-950/40 dark:text-red-400 dark:hover:bg-red-950/70',
  amber: 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-400 dark:hover:bg-amber-950/70',
  neutral: 'border-border bg-muted/40 text-foreground hover:bg-muted',
};

/**
 * One lifecycle action. A compact tinted button, not the stacked icon-over-label card it was:
 * the record is read at the modal's full width now, and three 150px-wide, 4-padding cards plus
 * a pill row below them cost ~120px of height — the tallest thing in a column whose whole job is
 * to fit on one screen. Colour still carries the meaning; the size no longer shouts it.
 */
function ActionCard({ tone, icon, label, onClick }: any) {
  return (
    <button
      onClick={onClick}
      className={`flex h-8 items-center gap-1.5 rounded-lg border px-3 text-[12px] font-semibold transition-colors ${CARD_TONE[tone] || CARD_TONE.neutral}`}
    >
      {icon}
      {label}
    </button>
  );
}

function PillButton({ icon, label, onClick }: any) {
  return (
    <button
      onClick={onClick}
      className="flex h-8 items-center gap-1.5 rounded-full border border-border px-3 text-[12px] font-medium text-foreground hover:bg-muted transition-colors"
    >
      {icon}{label}
    </button>
  );
}

/**
 * AlertActions — the lifecycle controls as ONE bar across the foot of the record.
 *
 * It used to sit inside the right-hand column, between the facts and the workflow history,
 * where it was a third thing competing for a column whose job is to be read top to bottom —
 * and where scrolling the history scrolled the actions off with it. Deciding what to DO about
 * an alert is the last step of reading one, so it belongs at the foot of the whole record and
 * pinned there: `sticky bottom-0` keeps Resolve reachable from anywhere in a long history.
 *
 * An action that has already been taken is replaced by a "done" chip showing the resulting
 * status, so the same step can't be fired twice. Re-assignment stays available from the list.
 */
export function AlertActions({ alert, userMap, onAck, onAssign, onResolve, onCancel, onReopen }: any) {
  const status = alert?.status || 'new';
  const terminal = status === 'resolved' || status === 'cancelled';
  const acked = !!alert?.acknowledged_by || status === 'acknowledged' || status === 'assigned' || terminal;
  const assigned = !!alert?.assigned_to;
  const nameOf = (id: any, name?: any) => name || (id ? userMap?.[id] || String(id).slice(0, 8) : null);

  const done: string[] = [];
  if (acked && !terminal) done.push(`Acknowledged${alert?.acknowledged_by ? ` by ${nameOf(alert.acknowledged_by)}` : ''}`);
  if (assigned) done.push(`Assigned to ${nameOf(alert.assigned_to) || 'operator'}`);
  if (status === 'resolved') done.push(`Resolved${alert?.resolved_by ? ` by ${nameOf(alert.resolved_by)}` : ''}`);
  if (status === 'cancelled') done.push('Marked false positive');

  return (
    <div className="sticky bottom-0 z-10 flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-t border-border bg-card/95 px-6 py-3 backdrop-blur-sm">
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">Alert Actions</h3>
      {done.map((d) => (
        <span key={d} className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
          <CheckIcon className="h-3 w-3" /> {d}
        </span>
      ))}
      <span className="flex-1" />
      {/* One wrapping row for every action, Assign included — it used to sit on a line of its
          own, which read as a different KIND of action rather than just a less common one. */}
      {terminal ? (
        <ActionCard tone="neutral" label="Reopen" onClick={onReopen} icon={<ArrowUturnLeftIcon className="h-4 w-4" />} />
      ) : (
        <>
          {!acked && (
            <ActionCard tone="amber" label="Acknowledge" onClick={onAck} icon={<CheckIcon className="h-4 w-4" />} />
          )}
          <ActionCard tone="emerald" label="Resolve" onClick={onResolve} icon={<CheckCircleIcon className="h-4 w-4" />} />
          <ActionCard tone="red" label="False Positive" onClick={onCancel} icon={<XCircleIcon className="h-4 w-4" />} />
          {!assigned && (
            <PillButton label="Assign" onClick={onAssign} icon={<UserPlusIcon className="h-3.5 w-3.5" />} />
          )}
        </>
      )}
    </div>
  );
}

/**
 * One tab in the activity strip. The count rides in the tab rather than in the heading below
 * it, because the count is the reason to switch tabs — "2 comments" is what tells the reader
 * there is anything behind the tab they aren't looking at.
 */
function ActivityTab({ active, label, count, onClick }: any) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-selected={active}
      role="tab"
      className={`-mb-px flex items-center gap-1.5 border-b-2 px-1 pb-2 text-[13px] font-semibold transition-colors ${
        active ? 'border-brand text-brand' : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      {label}
      {count > 0 && (
        <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${active ? 'bg-brand/15 text-brand' : 'bg-muted text-muted-foreground'}`}>
          {count}
        </span>
      )}
    </button>
  );
}

/**
 * AlertActivity — workflow history (lifecycle audit trail) and comments.
 *
 * Two tabs, not two stacked sections. Stacked, the comment box sat below however long the
 * audit trail happened to be, so the one part of the record an operator WRITES to was the part
 * they had to scroll to find — and a busy alert's history pushed it out of sight entirely.
 * Tabbed, each is one click away and the counts say which one has anything in it.
 */
export function AlertActivity({ nodeId, alert, alertId, userMap, currentUser }: any) {
  const [actions, setActions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'history' | 'comments'>('history');
  const [comment, setComment] = useState('');
  const [posting, setPosting] = useState(false);
  const [commentError, setCommentError] = useState('');

  const load = useCallback(() => {
    getNodeAlertActions(nodeId, alertId)
      .then((rows: any[]) => setActions(rows || []))
      .catch(() => setActions([]))
      .finally(() => setLoading(false));
  }, [nodeId, alertId]);

  // Refetch when the alert row itself changes, so an ack/assign/resolve done
  // from the actions above shows up in the timeline without a page refresh.
  const alertVersion = [alert?.status, alert?.updated_at, alert?.assigned_to, alert?.acknowledged_by].join('|');
  useEffect(() => { load(); }, [load, alertVersion]);

  const nameOf = (id: string | null, name: string | null) => name || (id ? userMap?.[id] || String(id).slice(0, 8) : 'Unknown');

  const postComment = async () => {
    const text = comment.trim();
    if (!text || posting) return;
    setPosting(true);
    setCommentError('');
    try {
      await addNodeAlertComment(nodeId, alertId, currentUser?.id, currentUser?.name, text);
      setComment('');
      load();
    } catch (e: any) {
      console.error(e);
      // Surface it — a failed post used to look exactly like a no-op.
      setCommentError(e?.response?.data?.message || e?.message || 'Failed to post comment');
    }
    finally { setPosting(false); }
  };

  const byTime = (a: any, b: any) => toMs(a.created_at) - toMs(b.created_at);
  // The detection itself isn't an audit row — synthesize it as the first entry.
  const detected = alert ? [{
    id: '__created', action: 'created', actor_name: 'System',
    created_at: alert.event_timestamp || alert.created_at,
  }] : [];
  const history = [...detected, ...actions.filter((a) => a.action !== 'commented').sort(byTime)];
  const comments = actions.filter((a) => a.action === 'commented').sort(byTime);

  return (
    <div className="mt-5">
      <div role="tablist" className="flex items-center gap-5 border-b border-border">
        <ActivityTab
          active={tab === 'history'}
          label={<span className="flex items-center gap-1.5"><ClockIcon className="h-4 w-4" /> Workflow history</span>}
          count={history.length}
          onClick={() => setTab('history')}
        />
        <ActivityTab
          active={tab === 'comments'}
          label={<span className="flex items-center gap-1.5"><ChatBubbleLeftEllipsisIcon className="h-4 w-4" /> Comments</span>}
          count={comments.length}
          onClick={() => setTab('comments')}
        />
      </div>

      {/* The one genuinely unbounded part of the record — capped so a long history scrolls
          inside itself instead of pushing the snapshot and the facts off the screen. */}
      <div className="max-h-[30vh] overflow-y-auto pr-1 pt-3">
        {tab === 'history' ? (
          loading ? (
            <p className="text-[12px] text-muted-foreground">Loading activity…</p>
          ) : history.length === 0 ? (
            <p className="text-[12px] text-muted-foreground">No activity yet.</p>
          ) : (
            <ul className="space-y-3">
              {history.map((ac, i) => (
                <li key={ac.id} className="relative pl-5">
                  {i < history.length - 1 && <span className="absolute left-[3px] top-3.5 bottom-[-0.75rem] w-px bg-border" />}
                  <span className={`absolute left-0 top-1.5 h-2 w-2 rounded-full ${HISTORY_DOT[ac.action] || 'bg-muted-foreground'}`} />
                  <p className="text-[13px] font-semibold text-foreground">
                    {HISTORY_TITLE[ac.action] || ACTION_LABEL[ac.action] || ac.action}
                    {ac.action === 'assigned' && <> → {nameOf(ac.target_id, ac.target_name)}</>}
                  </p>
                  <p className="text-[12px] text-muted-foreground">
                    {nameOf(ac.actor_id, ac.actor_name)} · {histTime(ac.created_at)}
                  </p>
                  {ac.note && (
                    <p className="mt-1 rounded-lg bg-muted/60 px-2 py-1 text-[12px] text-foreground whitespace-pre-wrap">{ac.note}</p>
                  )}
                </li>
              ))}
            </ul>
          )
        ) : (
          <>
            {comments.length === 0 ? (
              <p className="text-[12px] text-muted-foreground">No comments yet — the first one is below.</p>
            ) : (
              <ul className="space-y-3">
                {comments.map((c) => (
                  <li key={c.id} className="flex gap-3">
                    <Avatar name={nameOf(c.actor_id, c.actor_name)} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <span className="text-[13px] font-semibold text-foreground">{nameOf(c.actor_id, c.actor_name)}</span>
                        <span className="text-[11px] text-muted-foreground">{relTime(c.created_at)}</span>
                      </div>
                      <p className="text-[13px] text-muted-foreground whitespace-pre-wrap break-words">{c.note}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-3 flex items-start gap-3">
              <Avatar name={currentUser?.name} />
              <div className="relative flex-1">
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); postComment(); }
                  }}
                  rows={2}
                  placeholder="Add a comment… (Enter to send)"
                  className="w-full resize-none rounded-xl border border-border bg-background px-3 py-2 pr-11 text-[13px] text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none"
                />
                <button
                  onClick={postComment}
                  disabled={!comment.trim() || posting}
                  title="Send comment"
                  className="absolute bottom-2 right-2 rounded-full bg-brand p-1.5 text-white hover:bg-brand/90 disabled:opacity-40 transition-colors"
                >
                  <PaperAirplaneIcon className="h-4 w-4" />
                </button>
                {commentError && <p className="mt-1 text-[11px] text-destructive">{commentError}</p>}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function Field({ label, value, wide }: any) {
  return (
    <div className={`flex flex-col gap-0.5 ${wide ? 'sm:col-span-2' : ''}`}>
      <dt className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">{label}</dt>
      <dd className="text-[13px] text-foreground font-medium break-words">{value || '—'}</dd>
    </div>
  );
}

/**
 * AlertInfoGrid — the alert's facts as one ruled table.
 *
 * A bare two-column label/value grid left every value floating in whitespace, and with nine of
 * them the eye had nothing to follow: "Assigned by" and the "—" belonging to "Acknowledged by"
 * ended up on the same reading line. Ruling the cells makes each fact a cell you can land on,
 * and boxing the lot makes the facts one object beside the snapshot rather than nine loose ones.
 *
 * `wide` items (a resolution note, a summary) take the full width. Rows are laid out here
 * rather than left to grid auto-flow so the rules never draw a border around a hole.
 */
export function AlertInfoGrid({ items }: { items: ({ label: string; value: any; wide?: boolean } | false | null | undefined)[] }) {
  const cells = items.filter(Boolean) as { label: string; value: any; wide?: boolean }[];

  // Walk the cells assigning (row, column), inserting a filler where a full-width item lands
  // mid-row, so every row is complete and every border has something on both sides of it.
  const laid: { cell?: typeof cells[number]; row: number; left: boolean; wide: boolean }[] = [];
  let row = 0;
  let col = 0;
  for (const cell of cells) {
    if (cell.wide && col === 1) {
      laid.push({ row, left: false, wide: false });
      row += 1;
      col = 0;
    }
    laid.push({ cell, row, left: col === 0, wide: !!cell.wide });
    if (cell.wide) { row += 1; col = 0; }
    else if (col === 0) { col = 1; }
    else { row += 1; col = 0; }
  }
  const lastRow = laid.length ? laid[laid.length - 1].row : 0;

  return (
    <div className="overflow-hidden rounded-xl border border-border">
      <dl className="grid grid-cols-2">
        {laid.map((slot, i) => (
          <div
            key={slot.cell ? slot.cell.label : `filler-${i}`}
            className={`min-w-0 px-3.5 py-2.5 ${slot.wide ? 'col-span-2' : ''} ${
              slot.row < lastRow ? 'border-b border-border' : ''
            } ${slot.left && !slot.wide ? 'border-r border-border' : ''}`}
          >
            {slot.cell && (
              <>
                <dt className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{slot.cell.label}</dt>
                <dd className="mt-0.5 break-words text-[14px] font-medium text-foreground">{slot.cell.value || '—'}</dd>
              </>
            )}
          </div>
        ))}
      </dl>
    </div>
  );
}

/**
 * The alert's own confidence, pinned to the snapshot it was measured on. A bar as well as the
 * number: "91%" alone makes an operator do the comparison in their head, and the whole reason
 * the figure is on the record is to be compared against the ones they have seen before.
 */
export function ConfidenceMeter({ value }: { value: number }) {
  const pct = Math.max(0, Math.min(100, Math.round(value * 100)));
  return (
    <span className="flex items-center gap-2.5">
      <span className="text-[10px] font-bold uppercase tracking-wider text-white/70">Confidence</span>
      <span className="h-1.5 w-20 overflow-hidden rounded-full bg-white/25">
        <span className="block h-full rounded-full bg-brand" style={{ width: `${pct}%` }} />
      </span>
      <span className="text-[13px] font-bold tabular-nums text-white">{pct}%</span>
    </span>
  );
}

export function ActionModal({ kind, onClose, onConfirm }: any) {
  const [text, setText] = useState('');
  const title = kind === 'resolve' ? 'Resolve alert' : 'Cancel alert (false positive)';
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-border bg-card shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-foreground mb-3">{title}</h2>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          placeholder={kind === 'resolve' ? 'Resolution note (optional)' : 'Reason (optional)'}
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none"
          autoFocus
        />
        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={onClose}
            className="h-9 px-4 rounded-lg border border-border text-[13px] text-foreground hover:bg-muted transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(text)}
            className="h-9 px-4 rounded-lg bg-brand text-white text-[13px] font-medium hover:bg-brand/90 transition-colors"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}
